import experiment from 'experiment';
import type { BaseDragPlugin } from '#packages/rEditor';

import { isSlotOpenerEnabled, reportSlotOpenedBI } from '../utils';
import type { SlotOpenerScope } from '../scope';
import { debouncedSetSlot, isMouseOverTheLine } from './utils';
import { getLineRectV3 } from '../components/utils';

/**
 * When a component is dragged between two others - creates a gap between them to make space and avoid overlap. Only for mobile.
 */
export const createSlotOpenerDragPlugin = (
  scope: SlotOpenerScope,
): BaseDragPlugin => ({
  init: ({ hooks, pluginFactoryScope }) => {
    const { editorAPI } = pluginFactoryScope;
    const { slotOpenerPrivateApi, store } = scope;

    if (!isSlotOpenerEnabled(editorAPI, pluginFactoryScope.selectedComp[0])) {
      return;
    }

    hooks.onDragStart.tap(() => {
      const allSlots = slotOpenerPrivateApi.getAllSlotsForComponent(
        pluginFactoryScope.selectedComp,
      );

      store.setAllSlotsInContainer(allSlots);
    });

    let prevMouseY = 0;
    hooks.beforeLayoutUpdate.tap(({ mouseCoordinates, layout }) => {
      if (mouseCoordinates.pageY === prevMouseY) {
        return;
      }
      const dragDirection = mouseCoordinates.pageY > prevMouseY ? 'down' : 'up';

      const slot = slotOpenerPrivateApi.findAvailableSlot(
        store.getAllSlotsInContainer(),
        layout,
        dragDirection,
        mouseCoordinates,
      );

      if (experiment.isOpen('se_slotOpenerV2')) {
        const prevSlot = store.getAvailableSlot();
        if (prevSlot && prevSlot.aboveComponent !== slot?.aboveComponent) {
          store.setAvailableSlot(slot);
        }

        if (!slot) {
          debouncedSetSlot.cancel();
        } else {
          debouncedSetSlot(scope, slot);
        }
      } else {
        store.setAvailableSlot(slot);
      }

      if (experiment.isOpen('se_slotOpenerV3') && slot) {
        const lineLayout = getLineRectV3(slot, store.getShouldOpenSlot());

        store.setShouldOpenSlot(
          isMouseOverTheLine(mouseCoordinates, lineLayout),
        );
      }

      prevMouseY = mouseCoordinates.pageY;
    });

    hooks.endDrag.tap(() => {
      const { selectedComp } = pluginFactoryScope;

      const availableSlot = store.getAvailableSlot();

      const shouldOpenSlot = experiment.isOpen('se_slotOpenerV3')
        ? store.getShouldOpenSlot()
        : true;

      if (availableSlot && selectedComp && shouldOpenSlot) {
        slotOpenerPrivateApi.openSlotAndPositionComp(
          selectedComp,
          availableSlot,
        );
        reportSlotOpenedBI(editorAPI, selectedComp, 'drag');
      }

      store.setAllSlotsInContainer([]);
      store.setAvailableSlot(null);
      store.setShouldOpenSlot(false);
    });
  },
});
